import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { lazy, Suspense } from "react";
import "@/App.css";

import Home from "./pages/Home";
import Article from "./pages/Article";
import Category from "./pages/Category";
import Search from "./pages/Search";
import Cuidados from "./pages/Cuidados";
import Sobre from "./pages/Sobre";
import Corporativo from "./pages/Corporativo";
import Educacional from "./pages/Educacional";
import Formaturas from "./pages/Formaturas";
import Intercambio from "./pages/Intercambio";
import Cruzeiros from "./pages/Cruzeiros";
import Internacional3em1 from "./pages/Internacional3em1";
import ViagensParaMulheres from "./pages/ViagensParaMulheres";
import TerceiraIdade from "./pages/TerceiraIdade";
import Portfolio from "./pages/Portfolio";
import PortfolioPrint from "./pages/PortfolioPrint";
import Privacidade from "./pages/Privacidade";
import Termos from "./pages/Termos";
import AuthorPage from "./pages/AuthorPage";
import WhatsAppFab from "./components/WhatsAppFab";
import StickyCoupon from "./components/StickyCoupon";
import { LangProvider } from "./lib/LangContext";

// Admin routes are lazy-loaded so the ~admin bundle is NOT downloaded by public visitors
const AdminLogin      = lazy(() => import("./pages/admin/Login"));
const AdminLayout     = lazy(() => import("./pages/admin/AdminLayout"));
const Dashboard       = lazy(() => import("./pages/admin/Dashboard"));
const Categories      = lazy(() => import("./pages/admin/Categories"));
const PostEditor      = lazy(() => import("./pages/admin/PostEditor"));
const AdminBacklog    = lazy(() => import("./pages/admin/Backlog"));
const AdminNewsletter = lazy(() => import("./pages/admin/Newsletter"));
const AdminAuthors    = lazy(() => import("./pages/admin/Authors"));
const AdminSeo        = lazy(() => import("./pages/admin/Seo"));
const AdminAds        = lazy(() => import("./pages/admin/Ads"));
const AdminCotacoes   = lazy(() => import("./pages/admin/Cotacoes"));
const AdminClientes   = lazy(() => import("./pages/admin/Clientes"));
const AdminCruzeiros  = lazy(() => import("./pages/admin/Cruzeiros"));
const AdminSettings   = lazy(() => import("./pages/admin/Settings"));
const CotacaoPublic   = lazy(() => import("./pages/CotacaoPublic"));

const AdminFallback = () => (
  <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#666", fontSize: 14 }}>
    Carregando painel administrativo…
  </div>
);

function PublicShell({ children }) {
  return (
    <>
      {children}
      <WhatsAppFab />
      <StickyCoupon />
    </>
  );
}

function App() {
  return (
    <div className="App">
      <LangProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton duration={5000} />
          <Routes>
            <Route path="/" element={<PublicShell><Home /></PublicShell>} />
            <Route path="/artigo/:slug" element={<PublicShell><Article /></PublicShell>} />
            <Route path="/categoria/:slug" element={<PublicShell><Category /></PublicShell>} />
            <Route path="/autor/:slug" element={<PublicShell><AuthorPage /></PublicShell>} />
            <Route path="/busca" element={<PublicShell><Search /></PublicShell>} />
            <Route path="/cuidados" element={<PublicShell><Cuidados /></PublicShell>} />
            <Route path="/sobre" element={<PublicShell><Sobre /></PublicShell>} />
            <Route path="/agencia-de-viagens-corporativa" element={<PublicShell><Corporativo /></PublicShell>} />
            <Route path="/corporativo" element={<Navigate to="/agencia-de-viagens-corporativa" replace />} />
            <Route path="/educacional" element={<PublicShell><Educacional /></PublicShell>} />
            <Route path="/formaturas" element={<PublicShell><Formaturas /></PublicShell>} />
            <Route path="/intercambio" element={<PublicShell><Intercambio /></PublicShell>} />
            <Route path="/cruzeiros" element={<PublicShell><Cruzeiros /></PublicShell>} />
            <Route path="/roteiros/internacional-3-em-1" element={<PublicShell><Internacional3em1 /></PublicShell>} />
            <Route path="/viagens-para-mulheres" element={<PublicShell><ViagensParaMulheres /></PublicShell>} />
            <Route path="/terceira-idade" element={<PublicShell><TerceiraIdade /></PublicShell>} />
            <Route path="/privacidade" element={<Navigate to="/politica-de-privacidade" replace />} />
            <Route path="/politica-de-privacidade" element={<PublicShell><Privacidade /></PublicShell>} />
            <Route path="/termos" element={<PublicShell><Termos /></PublicShell>} />

            {/* Portfolio: standalone, no Header/Footer/WhatsApp — corporate business card */}
            <Route path="/viagens-corporativas/portfolio" element={<Portfolio />} />
            <Route path="/viagens-corporativas/portfolio/imprimir" element={<PortfolioPrint />} />
            {/* Legacy /portfolio paths → 301-style client redirect to the new SEO-friendly URL */}
            <Route path="/portfolio" element={<Navigate to="/viagens-corporativas/portfolio" replace />} />
            <Route path="/portfolio/imprimir" element={<Navigate to="/viagens-corporativas/portfolio/imprimir" replace />} />

            {/* Cotação pública — link privado 24h, noindex */}
            <Route path="/cotacao/:slug" element={<Suspense fallback={<div className="p-8 text-center">Carregando…</div>}><CotacaoPublic /></Suspense>} />
            <Route path="/pass/hub/cotacao/:codigo/:ultimonome" element={<Suspense fallback={<div className="p-8 text-center">Carregando…</div>}><CotacaoPublic /></Suspense>} />
            <Route path="/pass/hub/cotacao/:codigo" element={<Suspense fallback={<div className="p-8 text-center">Carregando…</div>}><CotacaoPublic /></Suspense>} />

            {/* Admin: fully code-split. Never shipped to public visitors. */}
            <Route
              path="/admin"
              element={
                <Suspense fallback={<AdminFallback />}>
                  <AdminLogin />
                </Suspense>
              }
            />
            <Route
              path="/admin"
              element={
                <Suspense fallback={<AdminFallback />}>
                  <AdminLayout />
                </Suspense>
              }
            >
              <Route path="dashboard"   element={<Suspense fallback={<AdminFallback />}><Dashboard /></Suspense>} />
              <Route path="categorias"  element={<Suspense fallback={<AdminFallback />}><Categories /></Suspense>} />
              <Route path="backlog"     element={<Suspense fallback={<AdminFallback />}><AdminBacklog /></Suspense>} />
              <Route path="seo"         element={<Suspense fallback={<AdminFallback />}><AdminSeo /></Suspense>} />
              <Route path="analytics"   element={<Navigate to="/admin/dashboard" replace />} />
              <Route path="configuracoes" element={<Suspense fallback={<AdminFallback />}><AdminSettings /></Suspense>} />
              <Route path="anuncios"    element={<Suspense fallback={<AdminFallback />}><AdminAds /></Suspense>} />
              <Route path="cotacoes"    element={<Suspense fallback={<AdminFallback />}><AdminCotacoes /></Suspense>} />
              <Route path="clientes"    element={<Suspense fallback={<AdminFallback />}><AdminClientes /></Suspense>} />
              <Route path="cruzeiros"   element={<Suspense fallback={<AdminFallback />}><AdminCruzeiros /></Suspense>} />
              <Route path="newsletter"  element={<Suspense fallback={<AdminFallback />}><AdminNewsletter /></Suspense>} />
              <Route path="autores"     element={<Suspense fallback={<AdminFallback />}><AdminAuthors /></Suspense>} />
              <Route path="posts/novo"  element={<Suspense fallback={<AdminFallback />}><PostEditor /></Suspense>} />
              <Route path="posts/:id"   element={<Suspense fallback={<AdminFallback />}><PostEditor /></Suspense>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </LangProvider>
    </div>
  );
}

export default App;
